<template>
  <vs-popup
    class="sync-preview-popup"
    :title="popupTitle"
    :active="active"
    @close="onClose"
    @update:active="$emit('update:active', $event)"
  >
    <div v-if="loading" class="text-center py-8">
      <p class="text-grey">Se încarcă datele categoriei…</p>
    </div>

    <div v-else-if="errorMsg" class="text-danger py-4">
      {{ errorMsg }}
    </div>

    <div v-else-if="preview">
      <!-- Stock source radio -->
      <div class="mb-4">
        <div class="text-sm font-semibold mb-2" style="color:#475569">Sursă stoc</div>
        <vs-radio v-model="stockSource" vs-value="real" class="mr-4" @input="onSourceChange">Stoc real</vs-radio>
        <vs-radio v-model="stockSource" vs-value="proposed" @input="onSourceChange">Stoc propus (după oferte)</vs-radio>
      </div>

      <!-- Option fields picker -->
      <div class="mb-4">
        <div class="flex items-center mb-2">
          <div class="text-sm font-semibold" style="color:#475569">
            Opțiuni de variantă Shopify
            <span class="text-grey"> — alege până la 3 dimensiuni</span>
          </div>
          <vs-button
            v-if="preview.option_fields_override_applied"
            size="small"
            type="flat"
            color="primary"
            class="ml-3"
            :disabled="loading"
            @click="resetOptionFields"
          >
            Revino la detecție automată
          </vs-button>
        </div>
        <div class="option-picker">
          <label
            v-for="c in preview.candidate_option_fields"
            :key="c.field"
            class="option-chip"
            :class="{
              selected: selectedOptionFields.includes(c.field),
              disabled: !selectedOptionFields.includes(c.field) && selectedOptionFields.length >= 3,
              inert: c.distinct_count <= 1,
            }"
          >
            <input
              type="checkbox"
              :value="c.field"
              :checked="selectedOptionFields.includes(c.field)"
              :disabled="
                loading
                || (!selectedOptionFields.includes(c.field) && selectedOptionFields.length >= 3)
                || c.distinct_count === 0
              "
              @change="onToggleOption(c.field, $event.target.checked)"
            />
            <span class="label">{{ c.label }}</span>
            <span class="count">{{ c.distinct_count }} val.</span>
          </label>
        </div>
        <p class="text-xs text-grey mt-1">
          Shopify limitează la 3 opțiuni per produs. Dimensiunile nealese rămân disponibile ca metafields pe variante.
        </p>
      </div>

      <!-- Summary chips -->
      <p class="mb-3">
        <vs-chip color="primary">{{ preview.products.length }} variante</vs-chip>
        <vs-chip color="grey">Top N: {{ preview.top_n_used }} / {{ preview.total_active_products }} active</vs-chip>
        <vs-chip v-if="editedCount" color="warning">{{ editedCount }} cantități editate manual</vs-chip>
      </p>

      <!-- Variants table -->
      <div class="variant-table-wrap">
        <table class="variant-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Nume</th>
              <th>Dimensiuni</th>
              <th class="num">Stoc real</th>
              <th class="num">Stoc propus</th>
              <th class="num">Cantitate finală</th>
              <th class="num">Majorare %</th>
              <th class="num">Preț EXW (RON)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id" :class="{ edited: row.edited }">
              <td>{{ row.sku }}</td>
              <td>{{ row.name_ro || row.name_en || '—' }}</td>
              <td class="text-sm text-grey">{{ row.dimensions_summary || '—' }}</td>
              <td class="num">{{ row.stock.real_pieces }}</td>
              <td class="num">{{ row.stock.proposed_pieces }}</td>
              <td class="num">
                <input
                  type="number"
                  min="0"
                  step="1"
                  class="qty-input"
                  v-model.number="row.finalQty"
                  @input="row.edited = true"
                />
              </td>
              <td class="num text-grey">{{ row.majoration_percentage }}%</td>
              <td class="num"><b>{{ row.price_exw_ron }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-end mt-5">
        <vs-button color="grey" type="flat" :disabled="syncing" @click="onClose" class="mr-2">Anulează</vs-button>
        <vs-button color="primary" :disabled="syncing || !rows.length" @click="doSync">
          {{ syncing ? 'Se sincronizează…' : 'Sincronizează în Shopify' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: 'SyncPreviewModal',
  components: {},
  props: {
    active: { type: Boolean, default: false },
    categoryId: { type: [Number, String], default: null },
    categoryName: { type: String, default: '' }
  },
  data () {
    return {
      loading: false,
      syncing: false,
      errorMsg: null,
      preview: null,
      stockSource: 'real',
      rows: [],
      selectedOptionFields: [],
      userPickedOptions: false
    }
  },
  computed: {
    popupTitle () {
      return this.categoryName
        ? `Pregătește sincronizarea — ${this.categoryName}`
        : 'Pregătește sincronizarea'
    },
    editedCount () {
      return this.rows.filter(r => r.edited).length
    }
  },
  watch: {
    active (val) {
      if (val && this.categoryId) this.loadPreview()
      if (!val) this.reset()
    }
  },
  methods: {
    reset () {
      this.preview = null
      this.rows = []
      this.errorMsg = null
      this.stockSource = 'real'
      this.selectedOptionFields = []
      this.userPickedOptions = false
    },
    onClose () {
      if (this.syncing) return
      this.$emit('update:active', false)
    },
    sourceQty (row) {
      return this.stockSource === 'proposed' ? row.stock.proposed_pieces : row.stock.real_pieces
    },
    loadPreview () {
      this.loading = true
      this.errorMsg = null
      const payload = {
        idcategory: this.categoryId,
        optionFields: this.userPickedOptions ? this.selectedOptionFields : null
      }
      this.$store.dispatch('shopify/fetchSyncPreview', payload)
        .then(data => {
          this.preview = data
          if (!this.userPickedOptions) {
            this.selectedOptionFields = Array.isArray(data.option_fields) ? data.option_fields.slice() : []
          }
          this.rows = data.products.map(p => ({
            ...p,
            finalQty: this.stockSource === 'proposed' ? p.stock.proposed_pieces : p.stock.real_pieces,
            edited: false
          }))
        })
        .catch(err => {
          this.errorMsg = (err.response && err.response.data && err.response.data.message) || err.message
        })
        .finally(() => { this.loading = false })
    },
    onToggleOption (field, checked) {
      const next = this.selectedOptionFields.filter(f => f !== field)
      if (checked && next.length < 3) next.push(field)
      this.selectedOptionFields = next
      this.userPickedOptions = true
      this.loadPreview()
    },
    resetOptionFields () {
      this.userPickedOptions = false
      this.selectedOptionFields = []
      this.loadPreview()
    },
    onSourceChange () {
      for (const row of this.rows) {
        if (!row.edited) row.finalQty = this.sourceQty(row)
      }
    },
    buildStockOverrides () {
      const overrides = {}
      for (const row of this.rows) {
        if (!row.edited) continue
        const expected = this.sourceQty(row)
        const qty = Math.max(0, Math.round(Number(row.finalQty) || 0))
        if (qty !== expected) overrides[row.id] = qty
      }
      return overrides
    },
    doSync () {
      const stockOverrides = this.buildStockOverrides()
      this.syncing = true
      this.$store.dispatch('shopify/syncCategory', {
        idcategory: this.categoryId,
        stockSource: this.stockSource,
        stockOverrides,
        optionFields: this.userPickedOptions ? this.selectedOptionFields : null
      })
        .then(res => {
          this.$emit('synced', res)
          this.$emit('update:active', false)
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare sincronizare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
            time: 15000
          })
        })
        .finally(() => { this.syncing = false })
    }
  }
}
</script>

<style lang="scss" scoped>
.sync-preview-popup ::v-deep .vs-popup {
  width: 1100px;
  max-width: 96vw;
}
.variant-table-wrap {
  max-height: 50vh;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}
.variant-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  thead th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    text-align: left;
    padding: 8px 10px;
    font-weight: 600;
    color: #475569;
    border-bottom: 1px solid #e2e8f0;
    z-index: 1;
  }
  th.num, td.num { text-align: right; }
  tbody td {
    padding: 6px 10px;
    border-bottom: 1px solid #f1f5f9;
  }
  tbody tr:hover { background: #f8fafc; }
  tbody tr.edited { background: #fef3c7; }
  tbody tr.edited:hover { background: #fde68a; }
}
.qty-input {
  width: 80px;
  padding: 3px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  text-align: right;
  font-size: 13px;
  &:focus {
    outline: none;
    border-color: rgba(var(--vs-primary), 1);
  }
}
.option-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.option-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #f8fafc;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  input { margin: 0; cursor: pointer; }
  .label { color: #334155; font-weight: 500; }
  .count { color: #94a3b8; font-size: 11px; }
  &:hover:not(.disabled):not(.inert) { border-color: #7c3aed; }
  &.selected {
    background: rgba(var(--vs-primary), 0.08);
    border-color: rgba(var(--vs-primary), 1);
    .label { color: rgba(var(--vs-primary), 1); }
  }
  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &.inert {
    opacity: 0.4;
    cursor: not-allowed;
    input { cursor: not-allowed; }
  }
}
</style>
